import { PurchaseCard } from '@components'

import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { OrganizationMarket } from '../../../../../../../../types/organizationMarket'
import { useSubscriptionPlanPrices } from '../../../../hooks/useSubscriptionPlanPrices'
import { resolveSubscriptionPlan } from '../../../../utils/resolveSubscriptionPlan'
import { useSelectPlan } from '../../contexts/planSelectionContext'
import { useIsCurrentPlan } from '../../hooks/useIsCurrentPlan'
import { getPlanPriceSuffixTKey } from '../../utils/getPlanPriceSuffixTKey'
import { PaymentIntervalSwitchComponent } from '../PaymentIntervalSwitchComponent'

interface StandardPaidPlanCardProps {
  plan: SubscriptionPlan
  market?: OrganizationMarket
}

export const StandardPaidPlanCard = ({ plan, market = '' }: StandardPaidPlanCardProps): ReactElement => {
  const { t } = useTranslation()
  const [isYearlyPaymentChecked, setYearlyPaymentChecked] = useState(true)
  const { isMonthlyPeriodForced, selectPlan } = useSelectPlan()
  const { isCurrentPlan, isCurrentPlanOnYearlyPayment } = useIsCurrentPlan(plan)
  const { monthlyPrice, yearlyPricePerMonth, isLoading: isLoadingPrices } = useSubscriptionPlanPrices(plan)
  const isYearlyPaymentSelected = isMonthlyPeriodForced ? false : isYearlyPaymentChecked

  const { content: planContent } = useMemo(() => resolveSubscriptionPlan(plan, market || undefined), [plan, market])
  const {
    comparison = '',
    cta = '',
    description = '',
    features = [],
    featuresHeader = '',
    highlight = '',
    name,
  } = planContent

  const handleUpgradePlanButtonClick = useCallback(() => {
    const periodSelected = isYearlyPaymentSelected ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly

    selectPlan(plan, periodSelected)
  }, [isYearlyPaymentSelected, selectPlan, plan])

  useEffect(() => {
    if (isCurrentPlan) {
      setYearlyPaymentChecked(isCurrentPlanOnYearlyPayment)
    }
  }, [isCurrentPlan, isCurrentPlanOnYearlyPayment])

  return (
    <PurchaseCard highlightedText={t(highlight)}>
      <PurchaseCard.Header description={<Trans i18nKey={description}></Trans>} title={t(name)} />
      {isCurrentPlan ? (
        <PurchaseCard.Button disabled>{t('subscription.plan.current')}</PurchaseCard.Button>
      ) : (
        <PurchaseCard.Button onClick={handleUpgradePlanButtonClick}>{t(cta)}</PurchaseCard.Button>
      )}
      <PurchaseCard.PriceSection
        isLoading={isLoadingPrices}
        price={isYearlyPaymentSelected ? yearlyPricePerMonth : monthlyPrice}
        priceSuffix={t(getPlanPriceSuffixTKey(market))}
        pricePeriodNode={
          <PaymentIntervalSwitchComponent
            disabled={isCurrentPlan || isMonthlyPeriodForced}
            isInitiallyChecked={isYearlyPaymentSelected}
            togglePaymentFrequency={() => setYearlyPaymentChecked(!isYearlyPaymentChecked)}
          />
        }
      />
      <PurchaseCard.Features
        header={t(featuresHeader)}
        items={features.map((tKey) => ({ tKey }))}
        itemsLabel={t(comparison)}
      />
    </PurchaseCard>
  )
}
